/**
 * PermissionGuard - route-level access from the per-user module matrix.
 */
import { type ReactNode } from "react";

import { ShieldX } from "lucide-react";

import {
  type PermissionCapability,
} from "@/features/manifest/model/permissionModuleMap";
import { useModulePermissions } from "@/features/manifest/model/useModulePermission";

interface PermissionGuardProps {
  permissionModule: string;
  capability?: PermissionCapability;
  anyOfCapabilities?: PermissionCapability[];
  label: string;
  children: ReactNode;
}

export function PermissionGuard({
  permissionModule,
  capability = "view",
  anyOfCapabilities,
  label,
  children,
}: PermissionGuardProps) {
  const { hasCapability, loading, fullAccess } = useModulePermissions();
  const allowed = anyOfCapabilities?.length
    ? anyOfCapabilities.some((cap) => hasCapability(permissionModule, cap))
    : hasCapability(permissionModule, capability);

  if (loading && !fullAccess) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Checking permissions…
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <ShieldX className="mb-4 h-12 w-12 text-muted-foreground" />
        <h2 className="mb-2 text-lg font-semibold">
          You don&apos;t have access to {label}
        </h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Your administrator has not granted you access to this area. Contact
          them if you need permission.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
